import axios from "axios";
import dayjs from "dayjs";
import { Alert } from "@mui/material";
import { useContext } from "react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { AppDataContext, ctxDefinition } from "../components/ctx";
import Loader from "../components/loader";
import { BrandButton } from "../components/form/buttons";
import UserMenu from "../components/userMenu";
import { ApiEndpoint } from "../components/utils";
import "./completedExams.scss";

interface Snapshot {
  id_sh: string | number;
  created_at: string;
}

interface SnapshotResponse {
  success: boolean;
  snapshots: Snapshot[];
  count: number;
  message?: string;
}

const CompletedExams = () => {
  const { userData } = useContext(AppDataContext) as ctxDefinition;
  const navigate = useNavigate();
  const hasSession = Boolean(userData?.token && userData?.userid);
  const {
    data: snapshots,
    error,
    isLoading,
    isFetching,
    refetch,
  } = useQuery<Snapshot[], Error>(
    ["completedExams", userData?.userid],
    async () => {
      const resp = await axios.post<SnapshotResponse>(
        ApiEndpoint + "wp-json/firstchoiceprep/v1/getSnapshots",
        {
          token: userData.token,
          user_id: userData.userid,
        },
      );
      if (!resp.data?.success || !Array.isArray(resp.data.snapshots)) {
        throw new Error(
          resp.data?.message ?? "Unable to load completed exams.",
        );
      }
      return resp.data.snapshots;
    },
    {
      enabled: hasSession,
      retry: false,
    },
  );

  return (
    <UserMenu>
      <section className="completedExamsPage">
        <header className="completedExamsHeader">
          <h1>Completed Exams</h1>
          <p>This list shows your previously completed exams.</p>
        </header>

        {!hasSession ? (
          <Alert severity="warning" className="completedExamsMessage">
            Your session is unavailable. Please sign in to view completed exams.
            <BrandButton onClick={() => navigate("/login", { replace: true })}>
              SIGN IN
            </BrandButton>
          </Alert>
        ) : isLoading ? (
          <div className="completedExamsLoading" aria-live="polite">
            <Loader />
          </div>
        ) : error ? (
          <Alert
            severity="error"
            className="completedExamsMessage"
            action={
              <BrandButton
                onClick={() => refetch()}
                disabled={isFetching}
                size="small"
              >
                RETRY
              </BrandButton>
            }
          >
            {error.message || "Unable to load completed exams."}
          </Alert>
        ) : snapshots?.length ? (
          <div className="completedExamsTableWrap">
            <table className="completedExamsTable">
              <thead>
                <tr>
                  <th scope="col">Exam Date</th>
                  <th scope="col" className="completedExamsActionHeading">
                    Results
                  </th>
                </tr>
              </thead>
              <tbody>
                {snapshots.map((snapshot) => (
                  <tr key={snapshot.id_sh}>
                    <td>{dayjs(snapshot.created_at).format("MMMM D, YYYY")}</td>
                    <td className="completedExamsAction">
                      <BrandButton
                        onClick={() =>
                          navigate(
                            `/exam-results?snapshotId=${encodeURIComponent(snapshot.id_sh)}`,
                          )
                        }
                      >
                        View Results
                      </BrandButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="completedExamsEmpty">No completed exams found.</p>
        )}
      </section>
    </UserMenu>
  );
};

export default CompletedExams;
